import React, { useState, useRef, useEffect, useMemo } from "react";

const CAREER_TRACKS = {
  medicalVA: {
    label: "Medical VA",
    roleName: "Medical Virtual Assistant",
    niche: "healthcare support",
    tools: ["EHR", "EMR", "Google Workspace", "HIPAA", "appointment scheduling", "patient records"],
    keywords: ["hipaa", "patient", "appointment", "ehr", "emr", "medical records", "insurance", "clinic", "confidential"],
    scenarios: ["handle patient information", "schedule appointments", "support a clinic", "verify patient details"],
  },
  medicalBilling: {
    label: "Medical Billing & Coding",
    roleName: "Medical Billing and Coding Specialist",
    niche: "medical billing, coding, claims, and insurance support",
    tools: ["ICD-10", "CPT", "HCPCS", "EHR", "clearinghouse", "insurance portals"],
    keywords: ["icd", "cpt", "claims", "insurance", "denial", "coding", "billing", "hipaa", "reimbursement"],
    scenarios: ["handle denied claims", "verify insurance", "review coding accuracy", "submit billing documentation"],
  },
  realEstate: {
    label: "Real Estate VA",
    roleName: "Real Estate Virtual Assistant",
    niche: "real estate admin, CRM, leads, and agent support",
    tools: ["Follow Up Boss", "KVCore", "Chime", "Mojo", "Vulcan7", "RedX"],
    keywords: ["crm", "leads", "listings", "appointments", "follow up", "prospecting", "real estate", "agent"],
    scenarios: ["manage leads", "update listings", "support an agent", "book appointments"],
  },
  goHighLevel: {
    label: "GoHighLevel",
    roleName: "GoHighLevel Virtual Assistant",
    niche: "funnels, CRM, automations, workflows, and client campaigns",
    tools: ["GoHighLevel", "funnels", "workflows", "pipelines", "triggers", "email campaigns"],
    keywords: ["gohighlevel", "ghl", "workflow", "automation", "pipeline", "funnel", "crm", "campaign"],
    scenarios: ["build a workflow", "fix a funnel", "manage pipelines", "set up automations"],
  },
  aiAutomation: {
    label: "AI Automation",
    roleName: "AI Automation VA",
    niche: "AI tools, workflow automation, and productivity systems",
    tools: ["ChatGPT", "Zapier", "Make", "Airtable", "Google Sheets", "Notion"],
    keywords: ["automation", "ai", "zapier", "make", "workflow", "prompt", "integration", "process"],
    scenarios: ["automate repetitive tasks", "connect apps", "create prompts", "improve workflows"],
  },
  accounting: {
    label: "Accounting",
    roleName: "Accounting Virtual Assistant",
    niche: "accounting support, reconciliation, reports, and financial organization",
    tools: ["QuickBooks", "Xero", "Excel", "Google Sheets", "invoices", "bank reconciliation"],
    keywords: ["quickbooks", "xero", "reconciliation", "invoice", "expenses", "reports", "excel", "accuracy"],
    scenarios: ["reconcile accounts", "prepare reports", "track expenses", "manage invoices"],
  },
  bookkeeping: {
    label: "Bookkeeping",
    roleName: "Bookkeeping Virtual Assistant",
    niche: "bookkeeping, transaction categorization, invoicing, and clean records",
    tools: ["QuickBooks", "Xero", "bank feeds", "invoices", "receipts", "spreadsheets"],
    keywords: ["bookkeeping", "transactions", "categorize", "receipts", "invoices", "reconcile", "quickbooks", "xero"],
    scenarios: ["categorize transactions", "organize receipts", "reconcile bank feeds", "prepare monthly books"],
  },
  generalVA: {
    label: "General VA (GVA)",
    roleName: "General Virtual Assistant",
    niche: "admin support, email, calendar, research, and daily operations",
    tools: ["Google Workspace", "Microsoft Office", "Trello", "Asana", "Slack", "Zoom"],
    keywords: ["admin", "calendar", "email", "research", "organized", "communication", "support", "assistant"],
    scenarios: ["manage calendars", "handle email", "perform research", "support daily operations"],
  },
  socialMedia: {
    label: "Social Media Management",
    roleName: "Social Media Manager VA",
    niche: "content planning, captions, scheduling, engagement, and analytics",
    tools: ["Canva", "Meta Business Suite", "Buffer", "Hootsuite", "Instagram", "TikTok"],
    keywords: ["content", "caption", "engagement", "schedule", "analytics", "canva", "instagram", "facebook"],
    scenarios: ["create a content calendar", "handle comments", "schedule posts", "review analytics"],
  },
  marketingVA: {
    label: "Marketing VA",
    roleName: "Marketing Virtual Assistant",
    niche: "campaign support, email marketing, lead generation, and marketing reports",
    tools: ["Mailchimp", "Canva", "Google Analytics", "CRM", "email campaigns", "landing pages"],
    keywords: ["marketing", "campaign", "leads", "email", "analytics", "brand", "content", "conversion"],
    scenarios: ["support a campaign", "track leads", "prepare marketing reports", "coordinate content"],
  },
  shopify: {
    label: "Shopify VA",
    roleName: "Shopify Virtual Assistant",
    niche: "Shopify store support, products, orders, inventory, and customer service",
    tools: ["Shopify Admin", "Oberlo", "Klaviyo", "inventory apps", "customer support", "fulfillment"],
    keywords: ["shopify", "orders", "products", "inventory", "fulfillment", "customers", "store", "ecommerce"],
    scenarios: ["upload products", "handle orders", "update inventory", "support customers"],
  },
  amazon: {
    label: "Amazon VA",
    roleName: "Amazon Virtual Assistant",
    niche: "Amazon seller support, listings, FBA, inventory, and marketplace operations",
    tools: ["Seller Central", "FBA", "Helium 10", "Jungle Scout", "PPC", "inventory reports"],
    keywords: ["amazon", "seller central", "fba", "listing", "inventory", "ppc", "keyword", "marketplace"],
    scenarios: ["optimize listings", "monitor inventory", "support FBA tasks", "research keywords"],
  },
  freight: {
    label: "Freight VA",
    roleName: "Freight Virtual Assistant",
    niche: "freight admin, logistics coordination, carrier support, and shipment tracking",
    tools: ["TMS", "load boards", "Excel", "email", "carrier portals", "tracking tools"],
    keywords: ["freight", "logistics", "shipment", "carrier", "tracking", "dispatch", "load", "broker"],
    scenarios: ["track shipments", "coordinate carriers", "update load details", "communicate with brokers"],
  },
  truckDispatch: {
    label: "Truck Dispatching VA",
    roleName: "Truck Dispatching Virtual Assistant",
    niche: "dispatching, load boards, brokers, rate confirmation, and driver support",
    tools: ["DAT", "Truckstop", "load board", "rate confirmation", "factoring", "TMS"],
    keywords: ["dispatch", "load board", "broker", "carrier", "driver", "rate confirmation", "factoring", "truck"],
    scenarios: ["book loads", "negotiate with brokers", "support drivers", "handle rate confirmations"],
  },
  dataEntry: {
    label: "Data Entry",
    roleName: "Data Entry Virtual Assistant",
    niche: "accurate data encoding, spreadsheet management, CRM updates, and quality checks",
    tools: ["Excel", "Google Sheets", "CRM", "data validation", "forms", "databases"],
    keywords: ["accuracy", "data", "excel", "spreadsheet", "validation", "entry", "quality", "detail"],
    scenarios: ["enter large data sets", "check accuracy", "clean spreadsheets", "update CRM records"],
  },
  construction: {
    label: "Construction Cost Estimating & Management",
    roleName: "Construction Cost Estimating and Management VA",
    niche: "construction estimating, takeoffs, project coordination, and cost tracking",
    tools: ["Planswift", "Bluebeam", "Excel", "takeoff", "BOQ", "project management tools"],
    keywords: ["estimate", "takeoff", "bluebeam", "planswift", "materials", "labor", "boq", "project"],
    scenarios: ["prepare estimates", "review drawings", "track project costs", "support construction managers"],
  },
  graphicDesign: {
    label: "Graphic Design VA",
    roleName: "Graphic Design Virtual Assistant",
    niche: "design assets, branding, social media graphics, layouts, and client revisions",
    tools: ["Canva", "Photoshop", "Illustrator", "Figma", "brand kits", "templates"],
    keywords: ["canva", "photoshop", "illustrator", "figma", "branding", "design", "layout", "typography"],
    scenarios: ["handle revisions", "create social graphics", "maintain branding", "deliver design files"],
  },
  wordpress: {
    label: "WordPress VA",
    roleName: "WordPress Virtual Assistant",
    niche: "WordPress website maintenance, plugins, content updates, and basic troubleshooting",
    tools: ["WordPress", "Elementor", "WooCommerce", "plugins", "themes", "cPanel"],
    keywords: ["wordpress", "elementor", "plugin", "theme", "woocommerce", "seo", "backup", "website"],
    scenarios: ["update plugins", "edit pages", "fix basic site issues", "maintain backups"],
  },
};

const createQuestionsForTrack = (track) => {
  const role = track.roleName;
  const toolText = track.tools.join(", ");
  const scenarioOne = track.scenarios[0];
  const scenarioTwo = track.scenarios[1];
  const scenarioThree = track.scenarios[2];

  return [
    {
      q: `Tell me about yourself as a ${role}.`,
      keywords: ["experience", "background", "reliable", "dedicated", "virtual assistant", ...track.keywords.slice(0, 4)],
      advice: `Introduce your experience, your ${track.niche} focus, and the value you bring to a client.`,
    },
    {
      q: `Why do you want to work as a ${role}?`,
      keywords: ["passion", "growth", "support", "client", "career", ...track.keywords.slice(0, 4)],
      advice: `Connect your motivation to the ${track.label} role, not just to earning online.`,
    },
    {
      q: `What do you know about the ${track.label} role?`,
      keywords: ["responsibilities", "support", "client", "process", ...track.keywords.slice(0, 5)],
      advice: `Show that you understand the daily responsibilities of a ${role}.`,
    },
    {
      q: `What experience do you have related to ${track.niche}?`,
      keywords: ["experience", "tasks", "training", "tools", ...track.keywords.slice(0, 5)],
      advice: `Mention specific tasks, tools, training, or practice projects related to ${track.label}.`,
    },
    {
      q: `What tools or software are you familiar with for ${track.label}?`,
      keywords: [...track.keywords, ...track.tools.map((t) => t.toLowerCase().split(" ")[0])],
      advice: `Mention tools such as ${toolText}, and explain how you use them.`,
    },
    {
      q: `How would you handle ${scenarioOne}?`,
      keywords: ["process", "organized", "accurate", "communication", ...track.keywords.slice(0, 5)],
      advice: `Answer with a step-by-step process. Interviewers trust candidates who explain their workflow clearly.`,
    },
    {
      q: `How would you handle ${scenarioTwo}?`,
      keywords: ["update", "confirm", "detail", "client", "professional", ...track.keywords.slice(0, 4)],
      advice: `Show that you communicate clearly and double-check important details.`,
    },
    {
      q: `How would you handle ${scenarioThree}?`,
      keywords: ["priority", "deadline", "tools", "quality", "report", ...track.keywords.slice(0, 4)],
      advice: `Focus on prioritization, quality control, and reporting updates to the client.`,
    },
    {
      q: `How do you make sure your work is accurate as a ${role}?`,
      keywords: ["check", "review", "accuracy", "quality", "details", "verify"],
      advice: "Strong VAs explain their quality-control process before submitting work.",
    },
    {
      q: `How do you communicate progress to a client in a ${track.label} role?`,
      keywords: ["updates", "daily", "report", "slack", "email", "transparent", "proactive"],
      advice: "Mention proactive updates, clear reports, and asking questions early.",
    },
    {
      q: "How do you handle multiple tasks and deadlines?",
      keywords: ["prioritize", "deadline", "calendar", "time blocking", "organized", "task list"],
      advice: "Explain how you rank tasks by urgency and business impact.",
    },
    {
      q: "How do you handle pressure or urgent tasks?",
      keywords: ["calm", "focus", "prioritize", "urgent", "plan", "communicate"],
      advice: "Show that you stay calm and communicate instead of panicking.",
    },
    {
      q: "How do you handle confidential client information?",
      keywords: ["confidential", "security", "privacy", "password", "nda", "protect"],
      advice: "Mention security, password management, privacy, and professionalism.",
    },
    {
      q: "Describe a time you made a mistake. How did you handle it?",
      keywords: ["ownership", "honesty", "fixed", "learned", "prevented", "solution"],
      advice: "Take ownership and explain how you prevented the mistake from happening again.",
    },
    {
      q: "How do you stay organized while working remotely?",
      keywords: ["calendar", "planner", "folders", "system", "checklist", "routine"],
      advice: "Describe your daily work system, not just your personality.",
    },
    {
      q: "Have you worked with U.S. clients before?",
      keywords: ["experience", "standards", "professional", "communication", "timezone", "culture"],
      advice: "If you are new, mention training and readiness for U.S. business standards.",
    },
    {
      q: "Are you comfortable working U.S. business hours?",
      keywords: ["yes", "night shift", "flexible", "available", "ready", "timezone"],
      advice: "Confirm your schedule, energy, and work setup are ready for U.S. hours.",
    },
    {
      q: "What is your home office setup?",
      keywords: ["internet", "mbps", "headset", "computer", "backup", "quiet", "power"],
      advice: "Mention stable internet, headset, quiet workspace, and backup power if available.",
    },
    {
      q: `Why should a client hire you as their ${role}?`,
      keywords: ["value", "reliable", "results", "skills", "support", "growth", ...track.keywords.slice(0, 3)],
      advice: "Sell your value clearly. Explain how you save time, reduce workload, or improve results.",
    },
    {
      q: `What are your long-term goals as a ${role}?`,
      keywords: ["long-term", "career", "growth", "stable", "learn", "improve"],
      advice: "Clients prefer someone who wants long-term growth, not just a temporary gig.",
    },
  ];
};

const QUESTION_BANK = Object.fromEntries(
  Object.entries(CAREER_TRACKS).map(([key, track]) => [key, createQuestionsForTrack(track)])
);

const FILLER_VARIANTS = [
  "um",
  "umm",
  "ummm",
  "uh",
  "uhh",
  "uhhh",
  "hmmm",
  "hmm",
  "like",
  "actually",
  "basically",
  "you know",
  "sort of",
  "kind of",
];

const shuffleAndPick = (items, count = 5) => {
  const shuffled = [...items].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const MockInterview = () => {
  const [selectedCareer, setSelectedCareer] = useState("realEstate");
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [voices, setVoices] = useState([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [pitch, setPitch] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [latency, setLatency] = useState(0);

  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  const selectedTrack = CAREER_TRACKS[selectedCareer];
  const currentData = questions[currentQuestionIndex];

  const averageScore = useMemo(() => {
    if (!sessionHistory.length) return 0;
    return (sessionHistory.reduce((a, b) => a + b, 0) / sessionHistory.length).toFixed(1);
  }, [sessionHistory]);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopPitchAnalysis = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setPitch(0);
  };

  const resetInterview = () => {
    stopTimer();
    stopPitchAnalysis();
    if (recognitionRef.current) recognitionRef.current.stop();
    setInterviewStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setTranscript("");
    setIsListening(false);
    setFeedback(null);
    setIsLoading(false);
    setTimeLeft(60);
    setSessionHistory([]);
    setIsFinished(false);
    setPitch(0);
    setStartTime(null);
    setLatency(0);
  };

  const startInterview = () => {
    const pickedQuestions = shuffleAndPick(QUESTION_BANK[selectedCareer], 5);
    setQuestions(pickedQuestions);
    setCurrentQuestionIndex(0);
    setTranscript("");
    setFeedback(null);
    setTimeLeft(60);
    setSessionHistory([]);
    setIsFinished(false);
    setInterviewStarted(true);
  };

  const generateDetailedTips = (matchedKeywords, wpm, lat, fillers, totalWords) => {
    const sections = { clarity: "", confidence: "", explanation: "", listening: "" };

    if (totalWords < 25) {
      sections.clarity = "CRITICAL FAILURE: Your response was far too brief for a professional setting. Interviewers may see this as lack of preparation or limited communication confidence. Aim for 25-50 words minimum.";
      sections.explanation = `LENGTH VIOLATION: You only provided ${totalWords} words. Add role-specific details, tools, and examples so your answer sounds professional and credible.`;
    } else {
      sections.clarity =
        wpm < 115
          ? "Pace Alert: Your speaking speed is below the 115 WPM threshold. Try to speak with more energy while staying clear."
          : wpm > 170
          ? "Pace Alert: You may be speaking too fast. Slow down slightly so the interviewer can process your answer."
          : "Professional Mastery: Your verbal delivery and pacing are strong for a client interview.";

      sections.explanation =
        matchedKeywords.length < 3
          ? `Content Gap: You need more ${selectedTrack.label}-specific keywords, tools, or task examples. Mention real tools and processes to sound more job-ready.`
          : `Expert Content: You included strong ${selectedTrack.label}-specific details. This builds trust and shows role readiness.`;
    }

    sections.confidence =
      fillers > 0
        ? `Confidence Leak: You used ${fillers} filler word(s). Use short silent pauses instead of saying um, uh, like, or you know.`
        : "Elite Confidence: You maintained a clean speech stream with zero obvious filler words.";

    sections.listening =
      lat < 1.2
        ? "Interruption Risk: You responded too quickly. Practice waiting a short beat before answering so you sound thoughtful."
        : "Perfect Attentiveness: You waited before responding, which shows respect for the interviewer.";

    return sections;
  };

  const evaluateAnswer = (duration, lat) => {
    if (!currentData) return;

    setIsLoading(true);

    setTimeout(() => {
      const text = transcript.toLowerCase().trim();
      const words = text.split(/\s+/).filter((w) => w.length > 0);
      const safeDuration = Math.max(duration, 1);
      const wpm = Math.round((words.length / safeDuration) * 60);
      const matchedKeywords = currentData.keywords.filter((k) => text.includes(k));

      let fillerCount = 0;
      words.forEach((w) => {
        if (FILLER_VARIANTS.includes(w.replace(/[^a-z]/gi, ""))) fillerCount += 1;
      });

      let clarityScore = wpm >= 115 && wpm <= 170 ? 10 : 6;
      if (words.length < 25) clarityScore = 3;

      let confidenceScore = Math.max(1, 10 - fillerCount * 3);
      if (words.length < 15) confidenceScore = Math.min(confidenceScore, 4);

      let explanationScore = (matchedKeywords.length / currentData.keywords.length) * 10;
      if (words.length < 25) explanationScore = 1;

      const listeningScore = lat >= 1.2 && lat <= 3.2 ? 10 : 4;

      const finalScore = Math.round(
        clarityScore * 0.25 +
          confidenceScore * 0.25 +
          listeningScore * 0.25 +
          explanationScore * 0.25
      );

      const tips = generateDetailedTips(
        matchedKeywords,
        wpm,
        lat.toFixed(1),
        fillerCount,
        words.length
      );

      setFeedback({
        score: finalScore,
        aiMetrics: {
          clarity: Math.round(clarityScore * 10),
          confidence: Math.round(confidenceScore * 10),
          listening: Math.round(listeningScore * 10),
          explanation: Math.round(explanationScore * 10),
        },
        detailedTips: tips,
        advice: currentData.advice,
        matchedKeywords,
        wordCount: words.length,
        wpm,
      });

      setIsLoading(false);
    }, 1500);
  };

  const stopAndAnalyze = () => {
    const durationInSeconds = startTime ? (Date.now() - startTime) / 1000 : 1;
    const finalLatency = startTime && latency ? (startTime - latency) / 1000 : 1.5;

    if (recognitionRef.current) recognitionRef.current.stop();
    setIsListening(false);
    stopTimer();
    stopPitchAnalysis();
    evaluateAnswer(durationInSeconds, finalLatency);
  };

  const startTimer = () => {
    setTimeLeft(60);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          stopAndAnalyze();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startPitchAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updatePitch = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        setPitch(dataArray.reduce((a, b) => a + b, 0) / bufferLength);
        animationFrameRef.current = requestAnimationFrame(updatePitch);
      };

      updatePitch();
    } catch (err) {
      console.error("Mic access denied", err);
    }
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser. Please use Google Chrome.");
      return;
    }

    setTranscript("");
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = "en-US";
    recognitionRef.current.onstart = () => setIsListening(true);
    recognitionRef.current.onresult = (event) => {
      setTranscript(Array.from(event.results).map((r) => r[0].transcript).join(" "));
    };
    recognitionRef.current.start();
  };

  const speakQuestion = () => {
    if (!currentData) return;

    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(currentData.q);
    const femaleVoice = voices.find(
      (v) => v.name.includes("Google US English") || v.name.includes("Samantha")
    );

    if (femaleVoice) utterance.voice = femaleVoice;

    utterance.onend = () => {
      setLatency(Date.now());
      setStartTime(Date.now());
      startListening();
      startTimer();
      startPitchAnalysis();
    };

    synth.speak(utterance);
  };

  useEffect(() => {
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      stopTimer();
      stopPitchAnalysis();
      if (recognitionRef.current) recognitionRef.current.stop();
      window.speechSynthesis.cancel();
    };
  }, []);

  const nextQuestion = () => {
    if (!feedback) return;

    const updatedHistory = [...sessionHistory, feedback.score];
    setSessionHistory(updatedHistory);

    if (currentQuestionIndex === questions.length - 1) {
      setIsFinished(true);
      return;
    }

    setTranscript("");
    setFeedback(null);
    setTimeLeft(60);
    setCurrentQuestionIndex((prev) => prev + 1);
  };

  const retryQuestion = () => {
    setTranscript("");
    setFeedback(null);
    setTimeLeft(60);
    setStartTime(null);
    setLatency(0);
  };

  const getHighlightedTranscript = () => {
    return transcript.split(" ").map((word, i) => {
      const cleanWord = word.toLowerCase().replace(/[^a-z]/gi, "");
      const isFiller = FILLER_VARIANTS.includes(cleanWord);
      return (
        <span
          key={i}
          className={
            isFiller
              ? "bg-red-500 text-white px-1 rounded mx-0.5 animate-bounce inline-block"
              : ""
          }
        >
          {word}{" "}
        </span>
      );
    });
  };

  if (!interviewStarted) {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6 md:p-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <img src="/favicon.ico" alt="Logo" className="w-16 h-16 mx-auto mb-5" />
            <h1 className="text-4xl font-black italic tracking-tighter mb-2">
              AI Mock Interview Coach
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm">
              Choose your target VA career. The coach will randomly select 5 interview questions from a 20-question bank for that specialization.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {Object.entries(CAREER_TRACKS).map(([key, track]) => (
              <button
                key={key}
                onClick={() => setSelectedCareer(key)}
                className={`text-left p-5 rounded-2xl border transition-all ${
                  selectedCareer === key
                    ? "bg-indigo-600 border-indigo-300 shadow-xl shadow-indigo-900/40"
                    : "bg-slate-900 border-slate-800 hover:border-indigo-500"
                }`}
              >
                <p className="font-black text-white text-lg">{track.label}</p>
                <p className="text-xs text-slate-300 mt-2 leading-relaxed">{track.niche}</p>
              </button>
            ))}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[10px] text-indigo-400 font-black uppercase tracking-widest mb-1">
                Selected Interview
              </p>
              <h2 className="text-2xl font-black text-white">{selectedTrack.label}</h2>
              <p className="text-xs text-slate-400 mt-1">
                20-question bank • 5 random questions per simulation • no extra AI API cost
              </p>
            </div>

            <button
              onClick={startInterview}
              className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 rounded-2xl font-black uppercase text-xs tracking-widest transition active:scale-95"
            >
              Start Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isFinished) {
    return (
      <div className="p-10 max-w-2xl mx-auto text-center bg-slate-950 rounded-[3rem] mt-10 shadow-2xl border border-indigo-500">
        <img src="/favicon.ico" alt="Logo" className="w-16 h-16 mx-auto mb-6" />
        <p className="text-indigo-400 font-black text-xs uppercase tracking-widest mb-2">
          {selectedTrack.label}
        </p>
        <h1 className="text-3xl font-black text-white mb-2 uppercase italic tracking-tighter">
          Final Evaluation
        </h1>
        <div className="bg-slate-900 p-8 rounded-[2rem] border border-slate-800 mb-8">
          <div className="text-8xl font-black text-white mb-4">{averageScore}</div>
          <p className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">
            Session Mean Rating
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={startInterview}
            className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-500 transition-all uppercase text-xs tracking-widest"
          >
            Practice Again 🔄
          </button>
          <button
            onClick={resetInterview}
            className="w-full py-5 bg-slate-800 text-slate-300 rounded-2xl font-black hover:bg-slate-700 transition-all uppercase text-xs tracking-widest"
          >
            Change Career
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto text-center font-sans bg-white rounded-[3rem] mt-10 shadow-2xl border border-slate-100 overflow-hidden relative">
      {isLoading && (
        <div className="absolute inset-0 z-50 bg-slate-950/90 flex flex-col items-center justify-center backdrop-blur-md">
          <div className="w-20 h-20 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-6"></div>
          <p className="text-indigo-400 font-black uppercase tracking-[0.5em] text-xs">
            Generating Strict Audit...
          </p>
        </div>
      )}

      <div className="flex justify-between items-center mb-8 px-4">
        <div className="flex items-center gap-3">
          <img src="/favicon.ico" alt="Logo" className="w-10 h-10 object-contain" />
          <div className="text-left">
            <h1 className="text-xl font-black text-slate-900 leading-none tracking-tighter italic">
              AI COACH
            </h1>
            <p className="text-slate-400 text-[9px] font-bold uppercase tracking-widest">
              {selectedTrack.label}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
            <div className="flex gap-0.5 h-4 items-end justify-end">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 bg-indigo-500 rounded-full transition-all duration-75"
                  style={{ height: `${pitch > i * 10 ? Math.min(100, pitch) : 10}%` }}
                ></div>
              ))}
            </div>
          </div>
          <div
            className={`px-4 py-3 rounded-2xl font-mono text-sm font-black shadow-inner ${
              timeLeft < 10 ? "bg-red-50 text-red-600" : "bg-slate-50 text-slate-400"
            }`}
          >
            {timeLeft}S
          </div>
        </div>
      </div>

      <div className="bg-slate-950 p-10 rounded-[2.5rem] mb-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-2 h-full bg-indigo-600"></div>
        <p className="text-left text-indigo-500 text-[10px] font-black uppercase mb-3 tracking-[0.3em]">
          Interviewer Prompt:
        </p>
        <p className="text-2xl text-white font-bold leading-tight text-left italic">
          "{currentData?.q}"
        </p>
      </div>

      <div className="flex justify-center mb-8 px-4">
        {!isListening ? (
          <button
            onClick={speakQuestion}
            disabled={!!feedback || isLoading}
            className={`w-full py-6 rounded-[1.5rem] font-black text-white shadow-2xl transition-all uppercase text-sm tracking-[0.2em] ${
              feedback || isLoading
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200"
            }`}
          >
            Start Voice Capture
          </button>
        ) : (
          <button
            onClick={stopAndAnalyze}
            className="w-full py-6 rounded-[1.5rem] font-black text-white bg-red-500 hover:bg-red-600 animate-pulse text-sm uppercase tracking-[0.2em] shadow-xl shadow-red-100"
          >
            End & Process Data
          </button>
        )}
      </div>

      <div className="text-left p-8 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200 min-h-[120px] mb-8 relative mx-4">
        <p className="text-slate-700 font-medium text-lg leading-relaxed">
          {transcript ? getHighlightedTranscript() : "Waiting for response (Min. 25 words)..."}
        </p>
        <div className="absolute top-2 right-4 text-[10px] font-black text-slate-300 uppercase tracking-widest">
          Word Count: {transcript.split(/\s+/).filter((w) => w.length > 0).length}
        </div>
        {isListening && (
          <div className="absolute bottom-4 right-4 flex gap-1 items-center">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
            <span className="text-[9px] font-black text-red-500 uppercase tracking-widest">
              Recording Active
            </span>
          </div>
        )}
      </div>

      {feedback && (
        <div className="p-8 bg-slate-950 rounded-[3rem] text-left shadow-2xl border border-indigo-500 animate-in zoom-in duration-500 mx-4">
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-slate-800">
            <div>
              <h3 className="font-black text-white text-3xl italic tracking-tighter uppercase leading-none">
                Performance Audit
              </h3>
              <p className="text-indigo-500 text-[10px] font-bold uppercase tracking-[0.3em] mt-2 italic">
                {selectedTrack.label} Question Audit
              </p>
            </div>
            <div className="bg-indigo-600 px-8 py-4 rounded-[1.5rem] shadow-lg shadow-indigo-500/40 text-center">
              <div className="text-5xl font-black text-white leading-none">{feedback.score}</div>
              <p className="text-[8px] font-bold text-indigo-200 uppercase mt-1">Grade</p>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 mb-6">
            <p className="text-[9px] font-black text-orange-400 uppercase mb-1">
              Role-Specific Advice
            </p>
            <p className="text-slate-300 text-xs leading-relaxed italic">{feedback.advice}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 text-white">
            <div className="space-y-6">
              {[
                ["1. Spoken Clarity", feedback.aiMetrics.clarity, "bg-emerald-500"],
                ["2. Confidence", feedback.aiMetrics.confidence, "bg-indigo-500"],
                ["3. Clear Explanations", feedback.aiMetrics.explanation, "bg-amber-500"],
                ["4. Active Listening", feedback.aiMetrics.listening, "bg-sky-500"],
              ].map(([label, value, color]) => (
                <div key={label}>
                  <div className="flex justify-between text-[10px] font-black text-slate-500 uppercase mb-2">
                    <span>{label}</span>
                    <span>{value}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color} transition-all duration-1000`}
                      style={{ width: `${Math.min(100, value)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {[
                ["Clarity Audit:", feedback.detailedTips.clarity, "border-emerald-500", "text-emerald-400"],
                ["Confidence Audit:", feedback.detailedTips.confidence, "border-indigo-500", "text-indigo-400"],
                ["Explanation Audit:", feedback.detailedTips.explanation, "border-amber-500", "text-amber-400"],
                ["Listening Audit:", feedback.detailedTips.listening, "border-sky-500", "text-sky-400"],
              ].map(([title, text, border, color]) => (
                <div key={title} className={`bg-slate-900 p-4 rounded-2xl border-l-4 ${border}`}>
                  <p className={`text-[9px] font-black ${color} uppercase mb-1`}>{title}</p>
                  <p className="text-slate-300 text-[10px] leading-relaxed italic">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={retryQuestion}
              className="flex-1 py-5 border border-slate-800 text-slate-500 rounded-2xl font-black hover:bg-slate-900 uppercase text-[10px] tracking-widest"
            >
              Retry Audit
            </button>
            <button
              onClick={nextQuestion}
              className="flex-[2] py-5 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-500 uppercase text-[10px] tracking-widest shadow-xl shadow-indigo-900/50 italic tracking-tighter"
            >
              Commit Progress
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterview;
